/**
 * Set a cookie with a name, value, and expiration days
 */
export const setCookie = (name: string, value: string, days = 365): void => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const cname = name + '=';
  const decodedCookie = decodeURIComponent(document.cookie || '');
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(cname) === 0) return c.substring(cname.length, c.length);
  }
  return null;
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
};

/**
 * Save filters to cookie as JSON
 */
export const saveFiltersToCookie = <T extends Record<string, any>>(
  key: string,
  filters: T,
  excludeKeys: string[] = ['page', 'limit', 'orderBy', 'orderType']
): void => {
  const filtersToSave: Partial<T> = {};
  
  Object.entries(filters).forEach(([k, v]) => {
    // Skip excluded keys and empty values
    if (!excludeKeys.includes(k) && v !== undefined && v !== '' && v !== null) {
      filtersToSave[k as keyof T] = v;
    }
  });

  // Only save if there are filters to persist
  if (Object.keys(filtersToSave).length > 0) {
    setCookie(key, JSON.stringify(filtersToSave));
  } else {
    deleteCookie(key);
  }
};

/**
 * Load filters from cookie
 */
export const loadFiltersFromCookie = <T extends Record<string, any>>(key: string): Partial<T> | null => {
  try {
    const saved = getCookie(key);
    if (saved) {
      return JSON.parse(saved) as Partial<T>;
    }
  } catch (error) {
    console.error('Failed to load filters from cookie:', error);
  }
  return null;
};
